/**
 * Pure routing logic for the in-app notification bell (NotificationsDialog).
 *
 * Given a notification row joined with its post / comment / event / actor,
 * returns either the React Router navigation target, the special
 * `'toast_deleted'` sentinel (caller shows a toast + nav to /feed), or null
 * (silent no-op — handler shouldn't navigate).
 *
 * The `comment` join must include `parent_comment_id` so the dropdown can
 * deep-link into a nested reply rather than just the parent post.
 */
export interface NotificationNav {
  path: string;
  state?: Record<string, any>;
}

export function isMentionNotification(n: Record<string, any>): boolean {
  return Boolean(n.mentioned_by_user_id && !n.type);
}

export function resolveDropdownNavigation(
  notification: Record<string, any>
): NotificationNav | 'toast_deleted' | null {
  // Mentions live in a separate table (user_mention_notifications) and always
  // link to a post.
  if (isMentionNotification(notification)) {
    if (!notification.post_id) return null;
    if (!notification.post) return 'toast_deleted';
    return { path: '/feed', state: { highlightPostId: notification.post_id } };
  }

  switch (notification.type) {
    case 'like':
    case 'comment':
    case 'share':
    case 'mention':
    case 'reply': {
      const targetPostId = notification.post_id || notification.comment?.post_id;
      if (!targetPostId) return null;
      if (!notification.post && !notification.comment) return 'toast_deleted';

      const state: Record<string, any> = { highlightPostId: targetPostId };
      const commentId = notification.comment_id ?? notification.comment?.id;
      if (commentId) {
        const parentId = notification.comment?.parent_comment_id;
        if (parentId) {
          state.highlightCommentId = parentId;
          state.highlightReplyId = commentId;
        } else {
          state.highlightCommentId = commentId;
        }
      }
      return { path: '/feed', state };
    }
    case 'follow':
      return { path: '/profile' };
    case 'referral_reward_given':
    case 'referral_reward_claimable':
      return { path: '/refer' };
    case 'new_event':
    case 'event_reminder':
      if (notification.event_id) {
        if (!notification.event) return 'toast_deleted';
        return { path: `/events?event=${notification.event_id}` };
      }
      return { path: '/events' };
    case 'new_message':
      if (notification.conversation_id) {
        return { path: `/chat?conversation=${notification.conversation_id}` };
      }
      return { path: '/chat' };
    // Lesson-scoped notifications: the daily reminder, and replies to a comment
    // under a lesson video. Both route by the row's OWN target, never by the
    // user's current day — that fallback made every unread reminder in the list
    // resolve to the same video (report 2026-08-02, "always lesson 33").
    case 'new_course_content':
    case 'comment_reply':
      // day_number >= 1, not just truthy: prep-day lesson rows are numbered <= 0
      // and have no /challenge/:id/day/:n target (a bare truthy check would send
      // day -1 to a route that cannot render it).
      if (notification.challenge_id && Number(notification.day_number) >= 1) {
        return { path: `/challenge/${notification.challenge_id}/day/${notification.day_number}` };
      }
      // Rows written before the target columns existed (migration
      // 20260802160010) carry no lesson. Home renders the current challenge
      // state and is one tap from today's lesson.
      return { path: '/' };
    default:
      if (notification.post_id) {
        return { path: '/feed', state: { highlightPostId: notification.post_id } };
      }
      return null;
  }
}
